import { G } from '@mobily/ts-belt';
import { keyBy } from 'lodash';

import { calculatePlanContributionTaxes } from '@Common/bo/calculate_plan_contribution_taxes';
import { isExemptFromCsgCrdsCampaign } from '@Common/bo/campaign_personal_assets';
import { flattenCampaignAllocationsPerChoice } from '@Common/bo/flatten_campaign_allocations_per_choice';
import {
  B2bCampaignType,
  CampaignAmountType,
  GetB2bCampaignAllocationOneResponse,
  ValueSharingBonusFiscalRule,
} from '@Common/types/bo/b2b/campaigns';
import {
  calculateProRataLateInterest,
  computeDaysAfterFiscalDeadline,
  resolveLateInterestsAmount,
} from '@Common/types/bo/b2b/campaigns/late_interests';
import { B2bCampaignActionHandler } from '@Common/types/bo/b2b/contracts';
import { TaxResidenceCode } from '@Common/types/bo/b2b/employees';
import { EmployeeSocialStatus } from '@Common/types/bo/b2b/employees_properties';
import { makeIntegerAmount } from '@Common/types/currency/currency';

import { BulkExportAllocationChoiceRow } from '@sh4res/bo-common/components/B2bCampaigns/bulkExportAllocations';
import {
  AllocationRecord,
  isAllocationAccepted,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/AllocationsConfirmationStep';

export type AllocationEstimationsPayload = {
  company_id: string;
  campaign: {
    type: B2bCampaignType;
    end_date: Date | null;
    fiscal_period_start: Date;
    fiscal_period_end: Date;
    is_automatic_late_payment_interests: boolean;
    late_payment_interests_override_amount: number | null;
    amount_type_input: CampaignAmountType;
    choice_collected_by: B2bCampaignActionHandler | null;
    value_sharing_bonus_fiscal_rule: ValueSharingBonusFiscalRule | null;
  };
  tmoPct: number | null;
  allocations: Array<{
    employee_id: number;
    identifier: string;
    amount: number;
    company_collected_choices: AllocationRecord['company_collected_choices'];
  }>;
  employees: Array<{
    firstname: string;
    lastname: string;
    registration_number?: string | null;
    identifier: string;
    employee_id: number;
    social_status: EmployeeSocialStatus;
    tax_residence_code: TaxResidenceCode;
  }>;
  matching_contributions?: Record<
    string,
    {
      total: number;
      choices: { id?: string; amount: number }[];
    }
  >;
};

export function prepareAllocationEstimations({
  allocations,
  employees,
  campaign,
  matching_contributions,
  company_id,
  tmoPct,
}: AllocationEstimationsPayload): BulkExportAllocationChoiceRow[] {
  const employeeMap = keyBy(employees, 'employee_id');
  const isExemptFromCsgCrds = isExemptFromCsgCrdsCampaign(campaign.type);

  const calculatedAllocations = allocations
    .filter((alloc) =>
      isAllocationAccepted({
        campaignType: campaign.type,
        amount: alloc.amount,
        companyCollectedChoices: alloc.company_collected_choices,
      }),
    )
    .map((alloc) => {
      const employee = employeeMap[alloc.employee_id];

      const primaryAmount = makeIntegerAmount(alloc.amount);
      const contributionDetails = calculatePlanContributionTaxes({
        socialStatus: employee.social_status,
        amount: primaryAmount,
        isNetAmountTaxContribution: campaign.amount_type_input === 'net',
        taxResidenceCode: employee.tax_residence_code,
        valueSharingBonusFiscalRule: campaign.value_sharing_bonus_fiscal_rule,
        isExemptFromCsgCrds,
      });

      const choicesWithMatchingContribution = (alloc.company_collected_choices ?? []).map(
        (choice) => {
          const choiceAmount = makeIntegerAmount(choice.amount ?? 0);
          const choiceContributionDetails = calculatePlanContributionTaxes({
            socialStatus: employee.social_status,
            amount: choiceAmount,
            isNetAmountTaxContribution: campaign.amount_type_input === 'net',
            taxResidenceCode: employee.tax_residence_code,
            valueSharingBonusFiscalRule: campaign.value_sharing_bonus_fiscal_rule,
            isExemptFromCsgCrds,
          });

          const mcAmount = calculatePlanContributionTaxes({
            socialStatus: employee.social_status,
            amount: makeIntegerAmount(
              matching_contributions?.[alloc.employee_id]?.choices.find((ch) => ch.id === choice.id)
                ?.amount ?? 0,
            ),
            isNetAmountTaxContribution: true,
            taxResidenceCode: employee.tax_residence_code,
            valueSharingBonusFiscalRule: campaign.value_sharing_bonus_fiscal_rule,
          });

          return {
            id: choice.id,
            allocation_type: choice.allocation_type ?? null,
            plan_id:
              choice.plan_id === 'payout' || choice.plan_id === 'ccb'
                ? null
                : (choice.plan_id ?? null),
            plan_type: choice.plan_type ?? null,
            plan_label: choice.plan_label ?? null,
            deductibility: choice.deductibility ?? null,
            instrument_id: choice.instrument_id ?? null,
            instrument_name: choice.instrument_name ?? null,
            risk_profile: choice.risk_profile ?? null,
            amount: choiceContributionDetails.userCurrencyAmount,
            gross_amount: choiceContributionDetails.grossUserCurrencyAmount,
            csg_amount: choiceContributionDetails.taxAmountDetails.csg_tax_amount,
            crds_amount: choiceContributionDetails.taxAmountDetails.crds_tax_amount,
            matching_contribution_gross_amount: mcAmount.grossUserCurrencyAmount,
            matching_contribution_amount: mcAmount.userCurrencyAmount,
          };
        },
      );

      return {
        id: employee.employee_id,
        campaign_id: 0,
        company_id: parseInt(company_id),
        employee_id: employee.employee_id,
        net_amount: contributionDetails.userCurrencyAmount,
        gross_amount: contributionDetails.grossUserCurrencyAmount,
        invested_amount: contributionDetails.userCurrencyAmount,
        entry_fee_amount: 0,
        net_invested_amount: contributionDetails.userCurrencyAmount,
        amount_in_days: null,
        cet_days: null,
        rtt_days: null,
        overtime_days: null,
        paid_leave_days: null,
        seniority_leave_days: null,
        untaken_rest_days: null,
        cet_amount: null,
        rtt_amount: null,
        overtime_amount: null,
        paid_leave_amount: null,
        seniority_leave_amount: null,
        untaken_rest_amount: null,
        invested_amount_in_days: null,
        invested_cet_days: null,
        invested_rtt_days: null,
        invested_overtime_days: null,
        invested_paid_leave_days: null,
        invested_seniority_leave_days: null,
        invested_untaken_rest_days: null,
        matching_contribution_amount: matching_contributions?.[alloc.employee_id]?.total ?? 0,
        payout_amount: (alloc.company_collected_choices ?? [])
          .filter((choice) => choice.allocation_type === 'payout')
          .reduce((acc, choice) => acc + (choice.amount ?? 0), 0),
        late_interests_amount: 0,
        identifier: employee.identifier,
        firstname: employee.firstname,
        lastname: employee.lastname,
        registration_number: employee.registration_number ?? null,
        csg_amount: contributionDetails.taxAmountDetails.csg_tax_amount,
        crds_amount: contributionDetails.taxAmountDetails.crds_tax_amount,
        social_status: employee.social_status,
        tax_residence_code: employee.tax_residence_code,
        company_collected_choices: choicesWithMatchingContribution,
      };
    });

  let finalAllocations: GetB2bCampaignAllocationOneResponse[] = [];

  if (
    campaign.is_automatic_late_payment_interests ||
    (G.isNotNullable(campaign.late_payment_interests_override_amount) &&
      campaign.late_payment_interests_override_amount > 0)
  ) {
    const totalAllocationsGross = calculatedAllocations.reduce(
      (acc, allocation) => acc + allocation.gross_amount,
      0,
    );

    const totalLateInterests = resolveLateInterestsAmount({
      amount: makeIntegerAmount(totalAllocationsGross),
      daysAfterFiscalDeadline: computeDaysAfterFiscalDeadline({
        endDate: new Date(campaign.end_date ?? new Date()),
        fiscalPeriodStart: new Date(campaign.fiscal_period_start),
        fiscalPeriodEnd: new Date(campaign.fiscal_period_end),
        campaignType: campaign.type,
        choiceCollectedBy: campaign.choice_collected_by,
      }),
      tmoPct,
      isAutomatic: campaign.is_automatic_late_payment_interests,
      overrideAmount: campaign.late_payment_interests_override_amount,
    });

    finalAllocations = calculatedAllocations.map(
      (allocation: (typeof calculatedAllocations)[number]) => {
        // Same as the backend import: nothing to split when there are no late interests or no gross.
        const lateInterestsProRata =
          totalLateInterests > 0 && totalAllocationsGross > 0
            ? calculateProRataLateInterest({
                allocationGross: allocation.gross_amount,
                totalAllocationsGross,
                totalLateInterest: totalLateInterests,
              })
            : 0;

        return {
          ...allocation,
          late_interests_amount: lateInterestsProRata,
        };
      },
    );
  } else {
    finalAllocations = calculatedAllocations;
  }

  return flattenCampaignAllocationsPerChoice(
    finalAllocations,
    campaign.value_sharing_bonus_fiscal_rule,
    false,
  );
}
